// src/components/CurriculumManagement.js
//
// Moderação do banco de currículos no admin (Fase G.1.6).
// Permite ao admin listar todos os currículos, alterar a visibilidade
// (public/private), excluir e exportar em CSV.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Papa from 'papaparse';
import {
  CATEGORY_LABELS,
  VISIBILITY_LABELS,
  AVAILABILITY_TYPES,
} from '../utils/resumeConstants';
import './UsersManagement.css';

const CurriculumManagement = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'resumes'));
        setResumes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Erro ao carregar currículos:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return resumes.filter((r) => {
      if (visibilityFilter && (r.visibility || 'private') !== visibilityFilter) return false;
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (!term) return true;
      const haystack = [
        r.fullName,
        r.email,
        r.city,
        r.state,
        r.headline,
        ...(Array.isArray(r.skills) ? r.skills : []),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [resumes, searchTerm, visibilityFilter, categoryFilter]);

  const handleToggleVisibility = async (resume) => {
    const next = resume.visibility === 'public' ? 'private' : 'public';
    try {
      await updateDoc(doc(db, 'resumes', resume.id), { visibility: next });
      setResumes((prev) => prev.map((r) => (r.id === resume.id ? { ...r, visibility: next } : r)));
    } catch (err) {
      console.error('Erro ao atualizar visibilidade:', err);
    }
  };

  const handleDelete = async (resume) => {
    if (!window.confirm(`Excluir o currículo de ${resume.fullName || resume.id}? Esta ação é irreversível.`)) return;
    try {
      await deleteDoc(doc(db, 'resumes', resume.id));
      setResumes((prev) => prev.filter((r) => r.id !== resume.id));
    } catch (err) {
      console.error('Erro ao excluir currículo:', err);
    }
  };

  const handleExport = () => {
    const exportData = filtered.map((r) => ({
      'ID (uid)': r.id,
      Nome: r.fullName || '',
      Email: r.email || '',
      Telefone: r.phone || '',
      Visibilidade: VISIBILITY_LABELS[r.visibility || 'private'],
      Área: CATEGORY_LABELS[r.category] || r.category || '',
      Cidade: r.city || '',
      Estado: r.state || '',
      País: r.country || '',
      Nacionalidade: r.nationality || '',
      Habilidades: Array.isArray(r.skills) ? r.skills.join('; ') : '',
      Idiomas: Array.isArray(r.languages) ? r.languages.map((l) => `${l.name}(${l.level || ''})`).join('; ') : '',
      Disponibilidades: Array.isArray(r.availability?.types) ? r.availability.types.map((t) => AVAILABILITY_TYPES[t] || t).join('; ') : '',
      Imediato: r.availability?.immediate ? 'Sim' : 'Não',
      Remoto: r.availability?.remote ? 'Sim' : 'Não',
      Anexo: r.attachmentUrl || '',
    }));
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'curriculos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="user-management"><p>Carregando currículos...</p></div>;
  }

  return (
    <div className="user-management">
      <h2>Banco de Currículos</h2>
      <div className="controls" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <input
          type="text"
          placeholder="Procurar por nome, e-mail, habilidade..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          className="search-input"
        >
          <option value="">Todas as visibilidades</option>
          {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="search-input"
        >
          <option value="">Todas as áreas</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="export-button" onClick={handleExport}>
          Exportar currículos
        </button>
      </div>
      {filtered.length === 0 ? (
        <p className="text-gray-500">Nenhum currículo encontrado.</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Nome</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Área</th>
              <th className="p-2 text-left">Cidade/UF</th>
              <th className="p-2 text-left">Visibilidade</th>
              <th className="p-2 text-left">Anexo</th>
              <th className="p-2 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="p-2">{r.fullName || '-'}</td>
                <td className="p-2">{r.email || '-'}</td>
                <td className="p-2">{CATEGORY_LABELS[r.category] || '-'}</td>
                <td className="p-2">{[r.city, r.state].filter(Boolean).join(' - ') || '-'}</td>
                <td className="p-2">{VISIBILITY_LABELS[r.visibility || 'private']}</td>
                <td className="p-2">
                  {r.attachmentUrl ? (
                    <a href={r.attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                      Baixar
                    </a>
                  ) : '-'}
                </td>
                <td className="p-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleVisibility(r)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      {r.visibility === 'public' ? 'Tornar privado' : 'Tornar público'}
                    </button>
                    <button
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      onClick={() => handleDelete(r)}
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CurriculumManagement;
